import { useState } from "react";
import { getPayoutWeekSummaryFile } from "../../../services/api/timekeeping";

/**
 * Save an XLSX blob as a browser download.
 *
 * @param {string} filename Suggested download filename.
 * @param {Blob} blob XLSX file.
 * @returns {void}
 */
function downloadXlsx(filename, blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Downloads one payroll week's summary workbook from the backend.
 *
 * @param {string} weekEnding Week-ending date (YYYY-MM-DD).
 * @returns {{download: Function, isDownloading: boolean, error: string}} Download controls.
 */
export function usePayoutWeekSummaryDownload(weekEnding) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState("");

  /**
   * Request the week-summary workbook and save it in the browser.
   *
   * @returns {Promise<void>}
   */
  const download = async () => {
    if (!weekEnding || isDownloading) {
      return;
    }

    setIsDownloading(true);
    setError("");
    try {
      const blob = await getPayoutWeekSummaryFile(weekEnding);
      downloadXlsx(`timesheet-summary-week-ending-${weekEnding}.xlsx`, blob);
    } catch (downloadError) {
      console.error("Error downloading payout week summary:", downloadError);
      setError("Unable to download week summary.");
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading, error };
}
